import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

/**
 * CookieConsent — GDPR/CCPA-friendly cookie consent banner. Fixed to
 * the bottom of the viewport on first visit; dismisses to a small
 * persistent "Cookie prefs" chip in the corner (via localStorage) so
 * users can change their mind later without diving into settings.
 *
 * Choices:
 *   • Accept all (functional + analytics)
 *   • Reject non-essential (functional only)
 *   • Learn more → /privacy
 *
 * Non-essential cookies (analytics, retargeting) MUST check
 * `window.__TAPIN_CONSENT` at their site before firing. Essential
 * cookies (auth session, CSRF) always run regardless of the choice
 * per GDPR Art. 6(1)(b) — necessary for the contract.
 */
const STORAGE_KEY = "tv_cookie_consent";

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p && typeof p === "object" ? p : null;
  } catch { return null; }
}

function writeConsent(next) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* quota */ }
  try {
    // Expose the consent state so other modules (analytics, etc.) can
    // gate their initialisation on it. Also emit a custom event so
    // React trees can subscribe without polling.
    window.__TAPIN_CONSENT = next;
    window.dispatchEvent(new CustomEvent("tapin:cookie-consent", { detail: next }));
  } catch { /* SSR */ }
}

export default function CookieConsent() {
  const [consent, setConsent] = useState(() => readConsent());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (consent) {
      try { window.__TAPIN_CONSENT = consent; } catch { /* SSR */ }
    }
  }, [consent]);

  const accept = () => {
    const next = { analytics: true, decided_at: Date.now(), version: 1 };
    writeConsent(next);
    setConsent(next);
  };
  const reject = () => {
    const next = { analytics: false, decided_at: Date.now(), version: 1 };
    writeConsent(next);
    setConsent(next);
  };
  const reset = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* quota */ }
    setConsent(null);
    setDismissed(false);
  };

  // First-visit banner — hidden once a decision is recorded.
  if (!consent) {
    return (
      <div
        data-testid="cookie-consent-banner"
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] w-[calc(100%-2rem)] max-w-2xl bg-black/90 backdrop-blur-xl border border-white/15 rounded-2xl px-4 py-3 shadow-[0_20px_60px_rgba(0,0,0,0.6)] text-white"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="text-xs sm:text-sm text-white/85 flex-1">
            We use essential cookies to keep you signed in, and optional analytics cookies to help us
            improve. You choose. See our <Link to="/privacy" className="text-tv-accent underline">Privacy Policy</Link>.
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={reject}
              data-testid="cookie-reject"
              className="text-[11px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/15"
            >
              Essential only
            </button>
            <button
              type="button"
              onClick={accept}
              data-testid="cookie-accept"
              className="text-[11px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full bg-tv-accent hover:bg-yellow-400 text-black"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Persistent tiny "Cookie prefs" chip so users can change their mind.
  if (dismissed) return null;
  return (
    <button
      type="button"
      onClick={reset}
      data-testid="cookie-prefs-chip"
      title="Change your cookie preferences"
      className="fixed bottom-3 left-3 z-40 text-[9px] uppercase tracking-widest font-black text-white/50 hover:text-white bg-black/60 border border-white/10 rounded-full px-2 py-1 hidden md:inline-flex items-center gap-1"
    >
      Cookie prefs
      <span
        role="button"
        tabIndex={0}
        aria-label="Hide cookie prefs chip"
        onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setDismissed(true); } }}
        className="cursor-pointer text-white/40 hover:text-white/70"
      >
        <X className="w-2.5 h-2.5" />
      </span>
    </button>
  );
}
